import { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, PackageSearch, Plus, Trash2, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@mustauto/ui/web';
import { CatalogAutocomplete, type CatalogItem } from '@/components/CatalogAutocomplete';
import { StockPartPicker, type PickedStockPart } from '@/components/StockPartPicker';
import { useCreateServiceOrder } from '@/hooks/useServiceOrders';
import { useWorkshopModules } from '@/hooks/useSettings';
import { ModuleRadioGroup } from '@/components/ModuleRadioGroup';
import {
  PACKAGE_MODULE,
  isWorkshopModule,
  openingModuleChoice,
  vehicleSizeFromBodyType,
  type VehicleSize,
  type WorkshopModule,
} from '@mustauto/types';
import { OSPackagePicker, useEstheticPackages } from '@/components/OSPackagePicker';
import { api } from '@/lib/api';
import { formatReais } from '@/lib/utils';
import {
  cleanPhone,
  formatApiError,
  isValidPlate,
  maskPhone,
  maskPlate,
  normalizePlate,
  packagesDurationHours,
  roundStockQuantity,
} from '@mustauto/utils';

interface TeamMember {
  id: string;
  name: string;
  /** Trabalha nos carros nesta oficina (perfil único, 5a): vem primeiro na lista. */
  worksOnCars?: boolean;
  activeOS: number;
}

interface VehicleResult {
  id: string;
  plate: string;
  make: string;
  model: string;
  yearModel: number;
  yearManuf: number;
  engineType: string | null;
  color: string | null;
}

interface ItemDraft {
  type: 'part' | 'labor' | 'outsourced';
  description: string;
  quantity: number;
  unitPrice: number;
  code?: string | null;
  /** Vínculo com o estoque — é o que faz a peça baixar quando a OS conclui. */
  origin?: 'stock' | 'supplier' | 'customer_brings';
  inventoryItemId?: string | null;
  /** Saldo em mãos quando a peça foi escolhida. Só para limitar a quantidade
   *  na tela; não vai para a API. */
  stockAvailable?: number;
}

/**
 * O prazo que vai para a API. O campo é uma DATA (`YYYY-MM-DD`), e
 * `new Date('2026-09-24')` é meia-noite UTC = 21h do dia ANTERIOR em Brasília:
 * a OS nascia com prazo de ontem.
 *
 * - Prazo calculado pelos pacotes: o instante exato (agora + horas).
 * - Data escolhida: 18h do dia, no fuso de quem está no painel (o "18h" dos
 *   atalhos do app) — e nunca antes de daqui a 1h, senão "hoje" escolhido às
 *   19h abriria a OS já atrasada.
 */
function deadlineInstant(date: string, autoHours: number | null): string | undefined {
  if (!date) return undefined;
  if (autoHours) return new Date(Date.now() + autoHours * 3_600_000).toISOString();
  const endOfDay = new Date(`${date}T18:00:00`).getTime();
  return new Date(Math.max(endOfDay, Date.now() + 3_600_000)).toISOString();
}

export function ServiceOrderNewPage() {
  const navigate = useNavigate();
  const { create, isCreating } = useCreateServiceOrder();

  // Tipo de serviço — a mesma regra da etapa 1 no app (`openingModuleChoice`):
  // oficina com um módulo só não escolhe nada, e a OS nasce SEM módulo, nunca
  // "mecânica" (o limiar de OS parada da oficina continua valendo para ela).
  const modulesQuery = useWorkshopModules();
  const opening = openingModuleChoice(
    (modulesQuery.data?.modules ?? [])
      .filter((m) => m.enabled && isWorkshopModule(m.module))
      .map((m) => m.module),
    modulesQuery.data?.defaultModule?.module,
  );
  const [pickedModule, setPickedModule] = useState<WorkshopModule | null>(null);
  const osModule = opening
    ? pickedModule && opening.choices.includes(pickedModule)
      ? pickedModule
      : opening.selected
    : undefined;

  // Vehicle state
  const [plate, setPlate] = useState('');
  const [vehicleId, setVehicleId] = useState<string | undefined>();
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [yearModel, setYearModel] = useState('');
  const [yearManuf, setYearManuf] = useState('');
  const [engineType, setEngineType] = useState('');
  const [color, setColor] = useState('');
  const [vehicleFound, setVehicleFound] = useState(false);
  const [vehicleSearching, setVehicleSearching] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');

  // Customer fields
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');

  // OS fields
  const [mechanicId, setMechanicId] = useState('');
  const [complaint, setComplaint] = useState('');
  const [notes, setNotes] = useState('');
  const [deadline, setDeadline] = useState('');
  const [deadlineTouched, setDeadlineTouched] = useState(false);
  /** Horas dos pacotes de onde o prazo foi calculado; `null` = escolhido à mão. */
  const [deadlineAutoHours, setDeadlineAutoHours] = useState<number | null>(null);

  // Pacotes de estética (F2.2): só na OS de estética. Com pacote, a
  // reclamação deixa de ser obrigatória e o prazo sai da duração.
  const isEstetica = osModule === PACKAGE_MODULE;
  const packagesQuery = useEstheticPackages();
  const [packageIds, setPackageIds] = useState<string[]>([]);
  const [vehicleSize, setVehicleSize] = useState<VehicleSize | null>(null);
  const [suggestedSize, setSuggestedSize] = useState<VehicleSize | null>(null);
  const chosenPackages = isEstetica
    ? (packagesQuery.data ?? []).filter((p) => packageIds.includes(p.id) && !p.pendingConfirmation)
    : [];
  const togglePackage = (id: string) => {
    const next = packageIds.includes(id) ? packageIds.filter((x) => x !== id) : [...packageIds, id];
    setPackageIds(next);
    if (!deadlineTouched) {
      const hours = packagesDurationHours((packagesQuery.data ?? []).filter((p) => next.includes(p.id)));
      // Data do campo (`type="date"`), no fuso de quem está no painel.
      if (hours) {
        const d = new Date(Date.now() + hours * 3_600_000);
        setDeadline(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
        setDeadlineAutoHours(hours);
      } else {
        setDeadline('');
        setDeadlineAutoHours(null);
      }
    }
  };

  // Items
  const [showItems, setShowItems] = useState(false);
  const [items, setItems] = useState<ItemDraft[]>([]);

  const [error, setError] = useState('');

  const teamQuery = useQuery<{ team: TeamMember[] }>({
    queryKey: ['team-list'],
    queryFn: async () => {
      const res = await api.get('/workshops/my/team');
      return res.data;
    },
  });

  const searchVehicle = useCallback(async (plateValue: string) => {
    const cleaned = normalizePlate(plateValue);
    if (cleaned.length < 7) return;

    setVehicleSearching(true);
    try {
      const res = await api.get(`/vehicles/by-plate/${cleaned}`);
      const v = res.data.vehicle as VehicleResult;
      setVehicleId(v.id);
      setMake(v.make);
      setModel(v.model);
      setYearModel(String(v.yearModel));
      setYearManuf(String(v.yearManuf));
      setEngineType(v.engineType ?? '');
      setColor(v.color ?? '');
      setVehicleFound(true);
    } catch {
      setVehicleId(undefined);
      setVehicleFound(false);
    } finally {
      setVehicleSearching(false);
    }
  }, []);

  const handlePlateChange = useCallback((value: string) => {
    // F-UI: maskPlate aplica ABC-1D23 display + sanitiza acentos/lowercase.
    // Estado guarda valor mascarado (com hífen) — submit normaliza antes
    // de mandar ao backend.
    const masked = maskPlate(value);
    setPlate(masked);
    if (normalizePlate(masked).length >= 7) {
      searchVehicle(masked);
    } else {
      setVehicleId(undefined);
      setVehicleFound(false);
      setMake('');
      setModel('');
      setYearModel('');
      setYearManuf('');
      setEngineType('');
      setColor('');
      setCatalogSearch('');
    }
  }, [searchVehicle]);

  const handleCatalogSelect = useCallback((item: CatalogItem) => {
    setMake(item.make);
    setModel(item.model);
    setCatalogSearch(`${item.make} ${item.model}`);
    if (item.yearFrom) setYearModel(String(item.yearFrom));
    if (item.engineSize) setEngineType(item.engineSize);
    // Carroceria do catálogo sugere o porte do pacote — só se ninguém escolheu.
    const size = vehicleSizeFromBodyType(item.bodyType);
    setSuggestedSize(size);
    if (size) setVehicleSize((current) => current ?? size);
  }, []);

  const addItem = useCallback(() => {
    setItems((prev) => [...prev, { type: 'labor', description: '', quantity: 1, unitPrice: 0 }]);
  }, []);

  const [stockPickerOpen, setStockPickerOpen] = useState(false);

  const addStockPart = useCallback((part: PickedStockPart) => {
    setItems((prev) => [
      ...prev,
      {
        type: 'part',
        description: part.description,
        quantity: 1,
        unitPrice: part.unitPrice,
        code: part.code,
        origin: part.origin,
        inventoryItemId: part.inventoryItemId,
        stockAvailable: part.available,
      },
    ]);
  }, []);

  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateItem = useCallback((index: number, field: keyof ItemDraft, value: string | number) => {
    setItems((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  }, []);

  const itemsTotal = items.reduce((sum, it) => sum + it.quantity * it.unitPrice, 0);

  const handleSubmit = async () => {
    setError('');

    // F-UI: validação strict de placa (regex Mercosul/old format + length).
    if (!isValidPlate(plate)) {
      setError('Placa inválida. Use o formato ABC-1234 (antigo) ou ABC-1D23 (Mercosul).');
      return;
    }
    if (!make || !model || !yearModel) {
      setError('Preencha marca, modelo e ano do veículo.');
      return;
    }
    if (!mechanicId) {
      setError('Selecione um mecânico.');
      return;
    }
    if (!complaint.trim() && chosenPackages.length === 0) {
      setError(
        isEstetica
          ? 'Escolha um pacote ou descreva o pedido do cliente.'
          : 'A reclamação do cliente é obrigatória.',
      );
      return;
    }

    try {
      const customerPhoneClean = cleanPhone(customerPhone);
      const result = await create({
        plate: normalizePlate(plate),
        make,
        model,
        yearModel: Number(yearModel),
        vehicleId,
        yearManuf: yearManuf ? Number(yearManuf) : undefined,
        engineType: engineType || undefined,
        color: color || undefined,
        mechanicId,
        complaint: complaint.trim() || undefined,
        notes: notes.trim() || undefined,
        // O campo é uma DATA (`YYYY-MM-DD`). `new Date('2026-09-24')` é
        // meia-noite UTC = 21h do dia anterior em Brasília: a OS nascia com
        // prazo de ontem. Vale o fim do expediente do dia escolhido, no fuso
        // de quem está no painel — o mesmo "18h" dos atalhos do app.
        deadline: deadlineInstant(deadline, deadlineAutoHours),
        items: showItems && items.length > 0
          ? items.map(({ stockAvailable: _ignored, ...it }) => ({
              ...it,
              unitPrice: Math.round(it.unitPrice * 100),
            }))
          : undefined,
        customerPhone: customerPhoneClean.length >= 10 ? customerPhoneClean : undefined,
        customerName: customerName.trim() || undefined,
        module: osModule,
        ...(chosenPackages.length > 0
          ? { packageIds: chosenPackages.map((p) => p.id), vehicleSize: vehicleSize ?? null }
          : isEstetica && vehicleSize
            ? { vehicleSize }
            : {}),
      });
      navigate({ to: '/service-orders/$osId', params: { osId: result.id } });
    } catch (err) {
      // F-UI Phase 5: surface server validation/business message em vez de
      // genérico. Ex: Zod issue por reclamação muito curta, ou regra de
      // negócio tipo "mecânico sem permissão".
      setError(formatApiError(err, 'Falha ao criar OS. Tente novamente.'));
      console.error('[ServiceOrderNew] create failed', err);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => navigate({ to: '/service-orders' })}
        >
          <ArrowLeft size={16} />
          Voltar
        </button>
        <h1 className="text-xl font-semibold">Nova Ordem de Serviço</h1>
      </div>

      {/* 0. Tipo de serviço — só para quem tem mais de um módulo ligado */}
      {opening && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tipo de serviço</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <ModuleRadioGroup
              name="module"
              label="Tipo de serviço da OS"
              options={opening.choices}
              value={osModule ?? null}
              onChange={setPickedModule}
            />
            <p className="text-xs text-muted-foreground">
              Define as etapas que a OS vai ter e o tempo até ela entrar nos alertas de OS parada.
            </p>
          </CardContent>
        </Card>
      )}
      {modulesQuery.isError && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-input bg-muted/50 px-4 py-3 text-sm"
        >
          <span>
            Não foi possível carregar os tipos de serviço. Se abrir agora, a OS fica sem tipo.
          </span>
          <button
            type="button"
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            onClick={() => void modulesQuery.refetch()}
          >
            Tentar de novo
          </button>
        </div>
      )}

      {/* 1. Vehicle */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Veículo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Placa *</label>
              <input
                type="text"
                maxLength={8}
                placeholder="ABC1D23 ou ABC-1234"
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 font-mono text-sm uppercase tracking-wider"
                value={plate}
                onChange={(e) => handlePlateChange(e.target.value)}
              />
            </div>
            {vehicleSearching && (
              <Loader2 size={18} className="mb-2 animate-spin text-muted-foreground" />
            )}
            {vehicleFound && (
              <span className="mb-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">Veículo encontrado</span>
            )}
          </div>

          {/* Catalog autocomplete — shown when vehicle not found by plate */}
          {!vehicleFound && (
            <div>
              <label className="text-xs text-muted-foreground">Buscar modelo no catálogo</label>
              <div className="mt-1">
                <CatalogAutocomplete
                  value={catalogSearch}
                  onChangeText={setCatalogSearch}
                  onSelect={handleCatalogSelect}
                  year={yearModel ? Number(yearModel) : undefined}
                  placeholder="Ex: Gol, Civic, Onix..."
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground">Marca *</label>
              <input
                type="text"
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                disabled={vehicleFound}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Modelo *</label>
              <input
                type="text"
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={vehicleFound}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Ano Modelo *</label>
              <input
                type="number"
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
                value={yearModel}
                onChange={(e) => setYearModel(e.target.value)}
                disabled={vehicleFound}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Ano Fabricação</label>
              <input
                type="number"
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
                value={yearManuf}
                onChange={(e) => setYearManuf(e.target.value)}
                disabled={vehicleFound}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Motor</label>
              <input
                type="text"
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
                value={engineType}
                onChange={(e) => setEngineType(e.target.value)}
                disabled={vehicleFound}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Cor</label>
              <input
                type="text"
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                disabled={vehicleFound}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2. Customer */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cliente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground">Telefone do cliente</label>
              <input
                type="tel"
                placeholder="(11) 99999-9999"
                maxLength={15}
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(maskPhone(e.target.value))}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Nome do cliente</label>
              <input
                type="text"
                placeholder="Nome completo"
                className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            O telefone vincula o veículo ao app do cliente (B2C).
          </p>
        </CardContent>
      </Card>

      {/* 3. Mechanic */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mecânico Responsável</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={mechanicId}
            onChange={(e) => setMechanicId(e.target.value)}
          >
            <option value="">Selecione um mecânico</option>
            {[...(teamQuery.data?.team ?? [])]
              .sort((a, b) => Number(Boolean(b.worksOnCars)) - Number(Boolean(a.worksOnCars)))
              .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.activeOS} OS hoje)
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* 2b. Pacote — só na OS de estética (F2.2) */}
      {isEstetica && (
        <OSPackagePicker
          packages={packagesQuery.data ?? []}
          selected={packageIds}
          size={vehicleSize}
          suggestedSize={suggestedSize}
          onToggle={togglePackage}
          onSizeChange={setVehicleSize}
          isLoading={packagesQuery.isLoading}
          isError={packagesQuery.isError}
          error={packagesQuery.error}
          onRetry={() => void packagesQuery.refetch()}
        />
      )}

      {/* 3. Description */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Descrição</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground">
              {chosenPackages.length > 0 ? 'Observações do cliente (opcional)' : 'Reclamação do cliente *'}
            </label>
            <textarea
              className="mt-1 block min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder={
                chosenPackages.length > 0
                  ? 'Pedido ou observação do cliente...'
                  : 'Descreva o problema relatado pelo cliente...'
              }
              value={complaint}
              onChange={(e) => setComplaint(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Observações internas</label>
            <textarea
              className="mt-1 block min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Notas internas da oficina (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* 4. Deadline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Prazo</CardTitle>
        </CardHeader>
        <CardContent>
          <input
            type="date"
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={deadline}
            onChange={(e) => {
              setDeadlineTouched(true);
              setDeadlineAutoHours(null);
              setDeadline(e.target.value);
            }}
          />
        </CardContent>
      </Card>

      {/* 5. Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Itens do Serviço</CardTitle>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="rounded"
                checked={showItems}
                onChange={(e) => setShowItems(e.target.checked)}
              />
              Já sei o que será feito
            </label>
          </div>
        </CardHeader>
        {showItems && (
          <CardContent className="space-y-3">
            {items.map((item, i) => (
              <div key={i} className="flex items-end gap-2">
                <div className="w-32">
                  <label className="text-xs text-muted-foreground">Tipo</label>
                  <select
                    className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={item.type}
                    onChange={(e) => updateItem(i, 'type', e.target.value)}
                  >
                    <option value="part">Peça</option>
                    <option value="labor">Mão de obra</option>
                    <option value="outsourced">Terceirizado</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Descrição</label>
                  <input
                    type="text"
                    className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={item.description}
                    onChange={(e) => updateItem(i, 'description', e.target.value)}
                  />
                </div>
                <div className="w-20">
                  <label className="text-xs text-muted-foreground">Qtd</label>
                  <input
                    type="number"
                    // Óleo em litro, vitrificador em ml: a quantidade pode ser fração.
                    // `any` mantém as setas andando de 1 em 1.
                    min={0.001}
                    step="any"
                    // Peça do estoque não passa do que existe na prateleira —
                    // mesma regra do app. Pedir 5 de um item com 2 debitaria 2
                    // e cobraria 5, e a diferença só apareceria no inventário.
                    max={item.origin === 'stock' ? item.stockAvailable : undefined}
                    className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={item.quantity}
                    // O `max` do HTML só bloqueia via validação de formulário,
                    // e aqui não há `<form>`: o envio é um `onClick`. Sem
                    // limitar no onChange, digitar 5 de um item com 2 salvava,
                    // cobrava 5 e debitava 2 — a divergência que a busca de
                    // estoque existe para acabar.
                    onChange={(e) => {
                      // Peça do estoque: 3 casas, como o débito — senão cobraria 1,2345 L
                      // e debitaria 1,235.
                      const typed =
                        item.origin === 'stock'
                          ? roundStockQuantity(Number(e.target.value))
                          : Number(e.target.value);
                      const cap = item.origin === 'stock' ? item.stockAvailable : undefined;
                      updateItem(i, 'quantity', cap != null ? Math.min(typed, cap) : typed);
                    }}
                  />
                </div>
                <div className="w-28">
                  <label className="text-xs text-muted-foreground">Valor unit.</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(i, 'unitPrice', Number(e.target.value))}
                  />
                </div>
                <div className="w-24 text-right text-sm font-medium">
                  {formatReais(item.quantity * item.unitPrice)}
                </div>
                <button
                  aria-label="Remover item"
                  className="mb-0.5 text-muted-foreground hover:text-destructive"
                  onClick={() => removeItem(i)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-4">
              {/* Buscar no estoque vem primeiro: é o caminho que mantém o
                  vínculo com a peça e faz a baixa acontecer na conclusão.
                  Digitar à mão continua valendo para o que não está cadastrado. */}
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                onClick={() => setStockPickerOpen(true)}
              >
                <PackageSearch size={14} />
                Buscar no estoque
              </button>
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground hover:underline"
                onClick={addItem}
              >
                <Plus size={14} />
                Adicionar item à mão
              </button>
            </div>

            {items.length > 0 && (
              <div className="border-t pt-2 text-right text-sm font-semibold">
                Total: {formatReais(itemsTotal)}
              </div>
            )}

            {stockPickerOpen && (
            <StockPartPicker
              onClose={() => setStockPickerOpen(false)}
              onPick={addStockPart}
              vehicleMake={make || undefined}
              vehicleModel={model || undefined}
            />
            )}
          </CardContent>
        )}
      </Card>

      {/* Error */}
      {error && (
        <p className="text-sm font-medium text-destructive">{error}</p>
      )}

      {/* Submit */}
      <div className="flex justify-end">
        <button
          className="flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          // Enquanto os tipos de serviço carregam, a oficina com mais de um
          // ainda não mostrou a escolha — abrir agora gravaria a OS sem tipo.
          disabled={isCreating || modulesQuery.isLoading}
          onClick={handleSubmit}
        >
          {isCreating && <Loader2 size={16} className="animate-spin" />}
          Abrir OS
        </button>
      </div>
    </div>
  );
}
